import { useApolloClient, useMutation, useQuery } from '@apollo/client'
import { useSnackbar } from 'app/_hooks/useSnackbar'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AddReplyDocument,
  CommentRepliesFragmentDoc,
  GetCommentRepliesDocument,
  RemoveReplyDocument,
  UpdateReplyDocument,
} from '~/__generated__/graphql'

export const useRepliesQuery = (commentId: string, skip: boolean) => {
  const client = useApolloClient()

  return useQuery(GetCommentRepliesDocument, {
    variables: { id: commentId },
    skip,
    onCompleted: (data) => {
      const totalCount = data.commentReplies?.totalCount
      if (totalCount == null) return
      client.cache.updateFragment(
        { id: `Comment:${commentId}`, fragment: CommentRepliesFragmentDoc },
        () => ({ replies: { totalCount } }),
      )
    },
  })
}

export const useRepliesMutation = (videoId: string, commentId: string) => {
  const showSnackbar = useSnackbar()
  const [addReply] = useMutation(AddReplyDocument)
  const [removeReply] = useMutation(RemoveReplyDocument)
  const [updateReply] = useMutation(UpdateReplyDocument)

  const { t } = useTranslation()

  const createReply = useCallback(
    async (contents: string, isSpoiler: boolean, replyId?: string) => {
      try {
        await addReply({
          variables: { videoId, commentId: commentId, replyId, isSpoiler, contents },
          refetchQueries: [GetCommentRepliesDocument],
          update: (cache) => {
            cache.updateFragment(
              {
                id: `Comment:${commentId}`,
                fragment: CommentRepliesFragmentDoc,
              },
              (data) => {
                return {
                  replies: {
                    totalCount: (data?.replies?.totalCount ?? 0) + 1,
                  },
                }
              },
            )
          },
        })
        showSnackbar(t('sent'))
      } catch (err) {
        console.error(err)
        showSnackbar(t('error_occurred'))
      }
    },
    [videoId, commentId, addReply, showSnackbar, t],
  )

  const deleteReply = useCallback(
    async (replyId: string) => {
      try {
        await removeReply({
          variables: { id: replyId },
          update: (cache) => {
            cache.evict({ id: 'ROOT_QUERY', fieldName: 'commentReplies' })
            cache.gc()
          },
        })
        showSnackbar(t('deleted'))
      } catch (err) {
        console.error(err)
        showSnackbar(t('error_occurred'))
      }
    },
    [showSnackbar, removeReply, t],
  )

  const editReply = useCallback(
    async (replyId: string, contents: string, isSpoiler: boolean) => {
      try {
        await updateReply({
          variables: { id: replyId, contents, isSpoiler },
          update: (cache, result) => {
            // キャッシュ内のリプライを更新
            const updatedReply = result.data?.updateReply?.reply
            if (updatedReply) {
              cache.modify({
                id: cache.identify(updatedReply),
                fields: {
                  contents: () => contents,
                  updatedAt: () => new Date().toISOString(),
                },
              })
            }
          },
        })
        showSnackbar(t('updated_comment'))
      } catch (err) {
        console.error(err)
        showSnackbar(t('error_occurred'))
      }
    },
    [updateReply, showSnackbar, t],
  )

  return {
    createReply,
    deleteReply,
    editReply,
  }
}
